<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import DoubleArrowIcon from '@/components/svg/DoubleArrowIcon.vue';
import styles from './expert-card-style.scss';

const b = bem('expert-card', styles);

export default {
  name: 'ExpertCard',
  components: {
    PencilIcon,
    DoubleArrowIcon,
  },
  props: {
    tab: {
      type: Object,
      required: true,
    },
    isFirst: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  emits: ['edit-image', 'edit-tab-title', 'edit-subtitle', 'edit-description', 'edit-button', 'edit-price'],
  data() {
    return {
      isOpen: this.isFirst,
    };
  },
  computed: {
    b() {
      return b;
    },
    imageSrc() {
      return this.tab.imageSrcMobile || this.tab.imageSrc || this.tab.mediaData || '';
    },
    videoSrc() {
      return this.tab.videoSrcMobile || this.tab.videoSrc || '';
    },
    isVideo() {
      return this.tab.isVideo || false;
    },
    tabTitle() {
      return this.tab.tabTitleMobile || this.tab.tabTitleMob || this.tab.tabTitle || '';
    },
    subtitle() {
      return this.tab.subtitleMobile || this.tab.titleMobile || this.tab.subtitle || this.tab.title || '';
    },
    hasSubtitle() {
      return !!this.subtitle;
    },
    description() {
      return this.tab.descriptionMobile || this.tab.description || '';
    },
    hasDescription() {
      return !!this.description;
    },
    buttonText() {
      return this.tab.buttonTextMobile || this.tab.buttonText || '';
    },
    hasButton() {
      return !!this.buttonText;
    },
    hasPrice() {
      return this.tab.withPrice && this.tab.price > 0;
    },
    price() {
      return this.tab.price || 0;
    },
    additionalDiscount() {
      return this.tab.additionalDiscount || 0;
    },
    afterText() {
      return this.tab.afterText || this.tab.perText || '';
    },
  },
  methods: {
    toggle() {
      if (!this.isEditMode) {
        this.isOpen = !this.isOpen;
      }
    },
    handleImageClick(event) {
      if (this.isEditMode) {
        event.stopPropagation();
        this.$emit('edit-image', this.index);
      } else {
        this.toggle();
      }
    },
    handleTabTitleEdit(event) {
      event.stopPropagation();
      this.$emit('edit-tab-title', this.index);
    },
    handleSubtitleEdit(event) {
      event.stopPropagation();
      this.$emit('edit-subtitle', this.index);
    },
    handleDescriptionEdit(event) {
      event.stopPropagation();
      this.$emit('edit-description', this.index);
    },
    handleButtonEdit(event) {
      event.stopPropagation();
      this.$emit('edit-button', this.index);
    },
    handlePriceEdit(event) {
      event.stopPropagation();
      this.$emit('edit-price', this.index);
    },
  },
  watch: {
    isFirst: {
      handler(newVal) {
        this.isOpen = newVal;
      },
      immediate: true,
    },
  },
};
</script>

<template>
  <div :class="b({ open: isOpen, first: isFirst })">
    <!-- Media wrap with gradient -->
    <div
      :class="b('media-wrap', { editable: isEditMode })"
      role="button"
      tabindex="0"
      @click="handleImageClick"
      @keyup.enter="handleImageClick"
    >
      <!-- Gradient overlay -->
      <div :class="b('gradient')" />

      <!-- Video -->
      <video
        v-if="isVideo && videoSrc"
        :class="b('media')"
        :src="videoSrc"
        autoplay
        muted
        loop
        playsinline
      />
      <!-- Image -->
      <img
        v-else-if="imageSrc"
        :class="b('media')"
        :src="imageSrc"
        alt=""
      />
      <!-- Placeholder -->
      <div v-else :class="b('media-placeholder')">
        <template v-if="isEditMode">
          <span :class="b('placeholder-plus')">+</span><br>
          CLICK TO UPLOAD
        </template>
      </div>

      <!-- Header with title and arrow -->
      <div :class="b('header')">
        <span :class="b('title')">
          {{ tabTitle }}
          <PencilIcon
            v-if="isEditMode"
            :class="b('title-edit')"
            color="#f4a460"
            :size="14"
            @click="handleTabTitleEdit"
          />
        </span>
        <span
          :class="b('arrow', { open: isOpen })"
          role="button"
          tabindex="0"
          @click.stop="toggle"
          @keyup.enter="toggle"
          @keyup.space="toggle"
        >
          <DoubleArrowIcon stroke="#eec586" :width="18" :height="24" />
        </span>
      </div>

      <!-- Subtitle on image -->
      <div
        v-if="hasSubtitle || isEditMode"
        :class="b('subtitle-wrap')"
        role="button"
        tabindex="0"
        @click.stop="handleSubtitleEdit"
        @keyup.enter="handleSubtitleEdit"
        @keyup.space="handleSubtitleEdit"
      >
        <span v-if="hasSubtitle" :class="b('subtitle')">
          {{ subtitle }}
        </span>
        <span v-else-if="isEditMode" :class="b('subtitle', { placeholder: true })">
          + Add subtitle
        </span>
        <PencilIcon
          v-if="isEditMode && hasSubtitle"
          :class="b('subtitle-edit')"
          color="#fff"
          :size="12"
        />
      </div>
    </div>

    <!-- Expandable content -->
    <transition name="slide">
      <div v-show="isOpen" :class="b('content')">
        <!-- Description -->
        <div
          v-if="hasDescription || isEditMode"
          :class="b('description-wrap', { editable: isEditMode })"
          role="button"
          tabindex="0"
          @click="handleDescriptionEdit"
          @keyup.enter="handleDescriptionEdit"
          @keyup.space="handleDescriptionEdit"
        >
          <p v-if="hasDescription" :class="b('description')">
            {{ description }}
          </p>
          <div v-else-if="isEditMode" :class="b('add-field')">
            + Add description
          </div>
        </div>

        <!-- Price -->
        <div
          v-if="hasPrice || isEditMode"
          :class="b('price-wrap', { editable: isEditMode })"
          role="button"
          tabindex="0"
          @click="handlePriceEdit"
          @keyup.enter="handlePriceEdit"
          @keyup.space="handlePriceEdit"
        >
          <div v-if="hasPrice" :class="b('price-block')">
            <span v-if="additionalDiscount" :class="b('price-old')">
              ${{ price }}
            </span>
            <span :class="b('price-value')">
              ${{ additionalDiscount ? price - additionalDiscount : price }}
            </span>
            <span v-if="afterText" :class="b('price-after')">
              {{ afterText }}
            </span>
          </div>
          <div v-else-if="isEditMode" :class="b('add-field')">
            + Add price
          </div>
        </div>

        <!-- Button -->
        <div
          v-if="hasButton || isEditMode"
          :class="b('button-wrap', { editable: isEditMode })"
          role="button"
          tabindex="0"
          @click="handleButtonEdit"
          @keyup.enter="handleButtonEdit"
          @keyup.space="handleButtonEdit"
        >
          <button v-if="hasButton" :class="b('button')">
            {{ buttonText }}
          </button>
          <div v-else-if="isEditMode" :class="b('add-field')">
            + Add button
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
